/* eslint-disable no-console */
import { Report } from 'powerbi-client';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ASQPowerBIReport from '../../../../common/components/molecules/ASQPowerBIReport';
import { expirationTokenInterval, useDepartureReportsQuery } from '../../../../common/hooks/backend';
import {
  getReportChangeListenerForFiltersAndSlicers,
  getSlicersFiltersSummary,
} from '../../../../common/utils/power-bi-filters';
import { Module } from '../../../../core/enums/module.enum';
import { withFeatureFlag } from '../../../../core/featureFlags/FeatureFlag';
import { FeatureFlagKey } from '../../../../core/featureFlags/featureFlagKeys';
import { useSetCurrentPowerBiFilters } from '../../../../core/powerBi/hooks';
import DashboardHeader from '../../dashboards/components/molecules/DashboardHeader';
import {
  getDepartureInfoMessageTranslateKey,
  getDepartureTitleTranslateKey,
  useDepartureDashboard,
} from '../dashboards';

function Dashboards(): ReactElement {
  const { t } = useTranslation('Departure');

  const {
    dashboard,
    pageName,
    onPageChange,
    onSlicerChange,
    onButtonClick,
    synchronizeAirportCodeAndSubscriptionAndPeriod,
  } = useDepartureDashboard(Module.Departure);

  const { data: embedRequest, isLoading } = useDepartureReportsQuery(undefined, {
    pollingInterval: expirationTokenInterval,
  });

  const infoMessageTranslateKey = getDepartureInfoMessageTranslateKey(dashboard);

  const [, setCurrentPowerBiFilters] = useSetCurrentPowerBiFilters();
  const onRendered = getReportChangeListenerForFiltersAndSlicers(setCurrentPowerBiFilters);

  const [complianceStatusFilter, setComplianceStatusFilter] = useState<any>(null);

  // Handle the compliance status filter logic for Regional and Unique subscriptions
  const handleComplianceStatusFilter = async (report: Report) => {
    const slicers = await getSlicersFiltersSummary(report);
    const slicer = slicers.find((s) => s.displayName === 'Subscription');
    if (slicer === undefined || slicer.values === undefined || slicer.values.length !== 1) return;

    const isRegionalOrUnique = slicer.values[0] === 'Unique' || slicer.values[0] === 'Regional';

    const filters = await report.getFilters();
    if (isRegionalOrUnique) {
      const csFilter = filters.find((f: any) => f.target.column === 'Compliance Status');

      if (csFilter !== undefined) {
        setComplianceStatusFilter(csFilter); // Save the filter to be able to restore it later
        const desiredFilters = filters.filter((f: any) => f.target.column !== 'Compliance Status');
        await report.setFilters(desiredFilters);
      }
    } else if (complianceStatusFilter !== null) {
      const csFilter = filters.find((f: any) => f.target.column === 'Compliance Status');
      if (csFilter === undefined) {
        await report.setFilters([complianceStatusFilter, ...filters]);
        setComplianceStatusFilter(null);
      }
    }
  };

  const handleCustomAnalysisValuePercentageBookmark = async (report: Report) => {
    const activePage = await report.getActivePage();
    if (activePage.displayName !== 'Custom Analysis') return;

    const slicers = await getSlicersFiltersSummary(report);
    const slicer = slicers.find((s) => s.displayName === 'Value');
    if (slicer === undefined || slicer.values === undefined || slicer.values.length !== 1) return;

    const isPercentage = slicer.values[0] === '%';

    if (isPercentage) {
      // Bookmark "Custom Analysis - Table No Total"
      await report.bookmarksManager.apply('f2a58b7098cdca0e3d8a');
    } else {
      // Bookmark "Custom Analysis - Table Total"
      await report.bookmarksManager.apply('3eb1c3b0b02e444b1dc4');
    }
  };

  const onChanged = async (report: Report) => {
    // Temporary code to hide Compliance Status filter for Regional and Unique subscriptions
    // It is currently only implemented in the report for Main subscription
    await handleComplianceStatusFilter(report);

    // temporary workaround to avoid hitting performance issues
    await handleCustomAnalysisValuePercentageBookmark(report);
  };

  return (
    <>
      {dashboard !== null && (
        <DashboardHeader
          title={t(getDepartureTitleTranslateKey(dashboard))}
          infoMessage={infoMessageTranslateKey !== null ? t(infoMessageTranslateKey) : undefined}
        />
      )}
      <ASQPowerBIReport
        embedRequest={embedRequest}
        isLoading={isLoading}
        pageName={pageName ?? undefined}
        onPageChange={onPageChange}
        onLoaded={synchronizeAirportCodeAndSubscriptionAndPeriod}
        onSlicerChange={onSlicerChange}
        onButtonClick={onButtonClick}
        onRendered={onRendered}
        onChanged={onChanged}
        showFilters
      />
    </>
  );
}

const DashboardsFeature = withFeatureFlag(Dashboards, FeatureFlagKey.DepartureReports);

export default DashboardsFeature;
